/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Select,
  Row,
  Col,
  DatePicker,
  Input,
  InputNumber,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TDropdownOptions, TResponseModel } from "../../../types/config";
import {
  eHTTPStatusCode,
  ePrivileges,
  eREPORTTYPE,
  eResultCode,
  MONTHS,
  QUARTERS,
  REPORTDURATION,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TReportPayload } from "../../../types";
import {
  GetCustomerList,
  GetOverallRecordsReport,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import dayjs from "dayjs";
import { UserContext } from "../../../context";
import { UNAUTHORIZEDREQUEST } from "../../../utils/constants";
import AuthUtil from "../../../utils/auth";
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
} from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import DateUtils from "../../../utils/date";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function ReportForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [selectedName, setSelectedName] = useState<string>("");
  const [reportTypeOptions, setReportTypeOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { label: number; value: number }[]
  >([]);
  const [fromDate, setFromDate] = useState(null);
  const [activeReportType, setActiveReportType] = useState<number>();
  const [reportDuration, setReportDuration] = useState<number>();
  const { onShowToast } = useToast();
  const context: any = useContext(UserContext);
  const { post } = useFetch();

  useEffect(() => {
    setReportTypeOptions(getReportOptions());
    setYearOptions(generateYearOptions());
    getCustomerList();
  }, []);

  const getCustomerList = async () => {
    try {
      const payload = {
        data: {
          pageSize: -1,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(GetCustomerList, payload);

      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setCustomerOptions(Utils.mapDropdownOptions(data));
          form.setFieldValue(
            "customerId",
            Utils.mapDropdownOptions(data)?.[0]?.value
          );
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching mapped partners:", error);
    }
  };

  // function to generate the year option
  function generateYearOptions() {
    const startYear = 2024;
    const currentYear = new Date().getFullYear(); // Get the current year
    const yearArray = [];

    for (let year = startYear; year <= currentYear; year++) {
      yearArray.push({ label: year, value: year });
    }

    return yearArray;
  }

  // function to generate the report type options on the basis of available privileges
  const getReportOptions = (): TDropdownOptions[] => {
    const availableReports: TDropdownOptions[] = [];

    // if (
    //   Utils.isUserHasAccess(
    //     context?.user?.privilegeList,
    //     ePrivileges.DOWNLOADSLAREPORT
    //   )
    // ) {
    availableReports.push({
      label: "Overall Records Report",
      value: eREPORTTYPE.OVERALLRECORDSREPORT,
    });
    // }

    return availableReports;
  };

  // Handle on submit event
  const onFinish = async (values: TReportPayload) => {
    try {
      const { monthDate, ...restValues } = values;
      const payload = {
        data: {
          monthDate: monthDate
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(monthDate).toDateString()
              )
            : null,
          ...restValues,
        },
      };
      setIsLoading(true);

      let reportEndpoint;

      switch (values.reportType) {
        case eREPORTTYPE.OVERALLRECORDSREPORT:
          reportEndpoint = GetOverallRecordsReport;
          break;
      }

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${reportEndpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Ensure JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            ...payload,
          }), // Convert body to JSON string
        }
      );

      // const response = await fetch(
      //   `${process.env.REACT_APP_PUBLIC_API_BASEURL}${
      //     values.reportType === eREPORTTYPE.SLAREPORT
      //       ? GetSLAReport
      //       : values.reportType === eREPORTTYPE.SERVICEINSTALLATIONREPORT ||
      //           values.reportType === eREPORTTYPE.INSTALLATIONREPORT
      //         ? GetServiceInstallationReport
      //         : values.reportType === eREPORTTYPE.YEARLYDEALERREPORT
      //           ? GetYearlyDealerReport
      //           : GetDealerActiveAssetReport
      //   }`,
      //   {
      //     method: "POST",
      //     headers: {
      //       "Content-Type": "application/json", // Ensure JSON payload
      //       ...AuthUtil.getToken(),
      //     },
      //     body: JSON.stringify({
      //       ...payload,
      //     }), // Convert body to JSON string
      //   }
      // );
      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        let fileName: string;

        switch (values.reportType) {
          case eREPORTTYPE.OVERALLRECORDSREPORT:
            fileName = "OVERALL_RECORDS_REPORT.xlsx";
            break;
        }
        // Extract content-disposition header to get the file name
        const contentDisposition = response.headers.get("Content-Disposition");

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1];
          }
        }

        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "Report downloaded successfully!",
        });
        props?.onCloseDrawer();
        props?.onRefreshList();
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "Error while downloading report, please try again later.",
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        size="large"
        name="reportForm"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
        onValuesChange={(changedValues) => {
          if (changedValues.fromDate) {
            setFromDate(changedValues.fromDate); // Update fromDate when changed
          }
        }}
      >
        <FormHeader>
          <Row
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                Download Report
              </h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="customerId"
                label="Customer"
                rules={[{ required: true, message: "Please choose customer" }]}
              >
                <Select
                  size="large"
                  disabled={isLoading}
                  showSearch
                  placeholder="Select a customer"
                  options={customerOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value, option) => {
                    const selectedOption = option as {
                      label: string;
                      value: string;
                    };
                    setSelectedName(selectedOption.label);
                  }}
                ></Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="reportType"
                label="Report Type"
                rules={[
                  { required: true, message: "Please select report type" },
                ]}
              >
                <Select
                  placeholder="Select report type"
                  options={reportTypeOptions}
                  onChange={(value: number) => setActiveReportType(value)}
                />
              </Form.Item>
            </Col>

            {/* REPORT DURATION */}
            {/* {activeReportType === eREPORTTYPE.INSTALLATIONCOSTREPORT ||
            activeReportType === eREPORTTYPE.AMCSUMMARYREPORT ? (
              <>
                <Col span={24}>
                  <Form.Item
                    name="reportDuration"
                    label="Report Duration"
                    rules={[
                      {
                        required: true,
                        message: "Please select report duration",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select report duration"
                      options={[
                        { value: REPORTDURATION.MONTH, label: "Month" },
                        { value: REPORTDURATION.QUARTER, label: "Quarter" },
                        { value: REPORTDURATION.YEAR, label: "Year" },
                      ]}
                      onChange={(value) => setReportDuration(value)}
                    />
                  </Form.Item>
                </Col>
              </>
            ) : null} */}

            {/* {activeReportType === eREPORTTYPE.SLAREPORT ||
            activeReportType === eREPORTTYPE.SERVICEINSTALLATIONREPORT ||
            activeReportType === eREPORTTYPE.INSTALLATIONREPORT ? (
              <Col span={24}>
                <Form.Item
                  name="monthDate"
                  label="Month"
                  rules={[
                    {
                      required: true,
                      message: "Please select month",
                    },
                  ]}
                >
                  <DatePicker
                    disabled={isLoading}
                    picker="month" // Use Month Picker
                    placeholder="Select month"
                    disabledDate={(current) => {
                      // Disable future months while keeping the current month selectable
                      return current && current > dayjs().endOf("month");
                    }}
                  />
                </Form.Item>
              </Col>
            ) : null} */}

            {/* {(activeReportType === eREPORTTYPE.INSTALLATIONCOSTREPORT ||
              activeReportType === eREPORTTYPE.AMCSUMMARYREPORT) &&
            reportDuration === REPORTDURATION.QUARTER ? (
              <Col span={24}>
                <Form.Item
                  name="quarter"
                  label="Quarter"
                  rules={[{ required: true, message: "Please select quarter" }]}
                >
                  <Select
                    placeholder="Select quarter"
                    options={[
                      { value: QUARTERS.Q1, label: "Q1" },
                      { value: QUARTERS.Q2, label: "Q2" },
                      { value: QUARTERS.Q3, label: "Q3" },
                      { value: QUARTERS.Q4, label: "Q4" },
                    ]}
                  />
                </Form.Item>
              </Col>
            ) : null} */}

            {/* {(activeReportType === eREPORTTYPE.INSTALLATIONCOSTREPORT ||
              activeReportType === eREPORTTYPE.AMCSUMMARYREPORT) &&
            reportDuration === REPORTDURATION.MONTH ? (
              <Col span={24}>
                <Form.Item
                  name="month"
                  label="Month"
                  rules={[{ required: true, message: "Please select month" }]}
                >
                  <Select
                    placeholder="Select month"
                    options={[
                      { value: MONTHS.JANUARY, label: "January" },
                      { value: MONTHS.FEBRUARY, label: "February" },
                      { value: MONTHS.MARCH, label: "March" },
                      { value: MONTHS.APRIL, label: "April" },
                      { value: MONTHS.MAY, label: "May" },
                      { value: MONTHS.JUNE, label: "June" },
                      { value: MONTHS.JULY, label: "July" },
                      { value: MONTHS.AUGUST, label: "August" },
                      { value: MONTHS.SEPTEMBER, label: "September" },
                      { value: MONTHS.OCTOBER, label: "October" },
                      { value: MONTHS.NOVEMBER, label: "November" },
                      { value: MONTHS.DECEMBER, label: "December" },
                    ]}
                  />
                </Form.Item>
              </Col>
            ) : null} */}

            {/* {activeReportType === eREPORTTYPE.DEALERACTIVEASSETREPORT ||
            activeReportType === eREPORTTYPE.YEARLYDEALERREPORT ? (
              <Col span={24}>
                <Form.Item
                  name="yearDate"
                  label="Year"
                  rules={[{ required: true, message: "Please select year" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select year"
                    options={yearOptions}
                  />
                </Form.Item>
              </Col>
            ) : null} */}

            {/* {activeReportType === eREPORTTYPE.INSTALLATIONCOSTREPORT ? (
              <Col span={24}>
                <Form.Item
                  name="year"
                  label="Year"
                  rules={[{ required: true, message: "Please select year" }]}
                >
                  <Select
                    // mode="multiple"
                    placeholder="Select year"
                    options={yearOptions}
                  />
                </Form.Item>
              </Col>
            ) : null} */}

            {/* {(activeReportType === eREPORTTYPE.INSTALLATIONCOSTREPORT ||
              activeReportType === eREPORTTYPE.AMCSUMMARYREPORT) &&
            reportDuration ? (
              <>
                <Col span={24}>
                  <Form.Item
                    name="taxHeading"
                    label="Tax Heading"
                    rules={[
                      { required: true, message: "Please enter tax heading" },
                    ]}
                  >
                    <Input placeholder="Enter tax heading" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="taxPercent"
                    label="Tax Percent"
                    rules={[
                      { required: true, message: "Please enter tax percent" },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter tax percent"
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      precision={2} // Allows up to 2 decimal places
                      // formatter={(value) => `${value}%`}
                      // parser={(value) =>
                      //   value?.replace("%", "") as unknown as number
                      // }
                    />
                  </Form.Item>
                </Col>
              </>
            ) : null} */}

            {/* <Col span={24}>
              <Form.Item
                name="toDate"
                label="To Date"
                rules={[
                  {
                    required: true,
                    message: "Please select date",
                  },
                ]}
                dependencies={["fromDate"]}
              >
                <DatePicker
                  format="DD-MMM-YYYY"
                  placeholder="Select date"
                  disabled={!fromDate}
                  disabledDate={(current) => {
                    return (
                      current &&
                      ((fromDate && current < dayjs(fromDate).startOf("day")) ||
                        current > maxDate)
                    );
                  }}
                />
              </Form.Item>
            </Col> */}
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              className="btnDimensions"
            >
              Download
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
